import React, { useState } from 'react';
import { 
  testApiConnection, 
  testAuthentication, 
  testWebSocket, 
  testServices,
  showConnectionInfo 
} from '../../utils/testConnection';

const TestConnection = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, success: 0, failed: 0 });

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { message, type, timestamp }]);
  };

  const clearLogs = () => {
    setLogs([]);
    setStats({ total: 0, success: 0, failed: 0 });
  };

  const handleTestBasicConnection = async () => {
    setLoading(true);
    addLog('🔍 Test de connexion de base...', 'header');
    
    const result = await testApiConnection();
    
    if (result) {
      addLog('✅ Connexion au backend réussie', 'success');
      setStats(prev => ({ ...prev, total: prev.total + 1, success: prev.success + 1 }));
    } else {
      addLog('❌ Échec de la connexion au backend', 'error');
      setStats(prev => ({ ...prev, total: prev.total + 1, failed: prev.failed + 1 }));
    }
    
    setLoading(false);
  };

  const handleTestAuthentication = async () => {
    setLoading(true);
    addLog('🔐 Test d\'authentification...', 'header');
    
    const email = prompt('Email:', 'admin@swiftai.com');
    const password = prompt('Mot de passe:', '');
    
    if (!email || !password) {
      addLog('❌ Credentials non fournis', 'error');
      setLoading(false);
      return;
    }
    
    const result = await testAuthentication(email, password);
    
    if (result) {
      addLog('✅ Authentification réussie', 'success');
      setStats(prev => ({ ...prev, total: prev.total + 1, success: prev.success + 1 }));
    } else {
      addLog('❌ Échec de l\'authentification', 'error');
      setStats(prev => ({ ...prev, total: prev.total + 1, failed: prev.failed + 1 }));
    }
    
    setLoading(false);
  };

  const handleTestWebSocket = () => {
    addLog('📡 Test WebSocket...', 'header');
    const result = testWebSocket();
    
    if (result) {
      addLog('✅ WebSocket initialisé', 'success');
      addLog('⏳ Vérification de la connexion en cours...', 'info');
    } else {
      addLog('❌ Échec de l\'initialisation WebSocket', 'error');
    }
  };

  const handleTestServices = async () => {
    setLoading(true);
    addLog('🔍 Test des services API...', 'header');
    
    const results = await testServices();
    
    Object.entries(results).forEach(([service, success]) => {
      if (success) {
        addLog(`✅ ${service}: OK`, 'success');
        setStats(prev => ({ ...prev, total: prev.total + 1, success: prev.success + 1 }));
      } else {
        addLog(`❌ ${service}: Échec`, 'error');
        setStats(prev => ({ ...prev, total: prev.total + 1, failed: prev.failed + 1 }));
      }
    });
    
    setLoading(false);
  };

  const handleShowInfo = () => {
    addLog('📋 Informations de connexion:', 'header');
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
    const wsUrl = process.env.REACT_APP_WS_URL || 'http://localhost:5000';
    const hasToken = !!localStorage.getItem('accessToken');
    
    addLog(`API URL: ${apiUrl}`, 'info');
    addLog(`WebSocket URL: ${wsUrl}`, 'info');
    addLog(`Token présent: ${hasToken ? 'Oui' : 'Non'}`, hasToken ? 'success' : 'warning');
  };

  const getLogClass = (type) => {
    const baseClass = 'p-3 rounded-lg mb-2 font-mono text-sm';
    switch (type) {
      case 'success':
        return `${baseClass} bg-green-100 text-green-800 border-l-4 border-green-500`;
      case 'error':
        return `${baseClass} bg-red-100 text-red-800 border-l-4 border-red-500`;
      case 'warning':
        return `${baseClass} bg-yellow-100 text-yellow-800 border-l-4 border-yellow-500`;
      case 'header':
        return `${baseClass} bg-gray-200 text-gray-800 border-l-4 border-gray-500 font-bold`;
      default:
        return `${baseClass} bg-blue-100 text-blue-800 border-l-4 border-blue-500`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-8 text-white">
            <h1 className="text-3xl font-bold mb-2">🚀 Test de Connexion Backend</h1>
            <p className="text-purple-100">Swift AI - Vérification de la connexion API</p>
          </div>
        </div>

        {/* Configuration */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">⚙️ Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                URL API
              </label>
              <input
                type="text"
                value={process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}
                readOnly
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg bg-gray-50 text-gray-700"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                URL WebSocket
              </label>
              <input
                type="text"
                value={process.env.REACT_APP_WS_URL || 'http://localhost:5000'}
                readOnly
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg bg-gray-50 text-gray-700"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">🧪 Tests Disponibles</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <button
              onClick={handleTestBasicConnection}
              disabled={loading}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              🔍 Connexion
            </button>
            <button
              onClick={handleTestAuthentication}
              disabled={loading}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              🔐 Auth
            </button>
            <button
              onClick={handleTestWebSocket}
              disabled={loading}
              className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-purple-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              📡 WebSocket
            </button>
            <button
              onClick={handleTestServices}
              disabled={loading}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              🔧 Services
            </button>
            <button
              onClick={handleShowInfo}
              className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-4 py-3 rounded-lg font-semibold hover:from-indigo-600 hover:to-indigo-700 transition-all transform hover:scale-105 shadow-lg"
            >
              📋 Info
            </button>
          </div>
          <div className="mt-3">
            <button
              onClick={clearLogs}
              className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-105 shadow-lg"
            >
              🗑️ Effacer les logs
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats.total > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="text-4xl font-bold text-purple-600">{stats.total}</div>
              <div className="text-sm text-gray-600 mt-2">Tests effectués</div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="text-4xl font-bold text-green-600">{stats.success}</div>
              <div className="text-sm text-gray-600 mt-2">Réussis</div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="text-4xl font-bold text-red-600">{stats.failed}</div>
              <div className="text-sm text-gray-600 mt-2">Échoués</div>
            </div>
          </div>
        )}

        {/* Logs */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">📊 Résultats</h2>
          <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Aucun test effectué. Cliquez sur un bouton pour commencer.
              </p>
            ) : (
              logs.map((log, index) => (
                <div key={index} className={getLogClass(log.type)}>
                  <span className="text-xs text-gray-500 mr-2">[{log.timestamp}]</span>
                  {log.message}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="fixed bottom-8 right-8 bg-white rounded-full shadow-2xl p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestConnection;
